<template>
    <div
        v-qa="'product-card'"
        class="product-card"
    >
        <div class="link-spacer"></div>

        <ThLink
            :to="url"
            class="th-link"
            @click="$emit('linkClicked')"
        >
            <div class="image-wrapper">
                <div
                    v-if="image"
                    ref="container"
                    class="carousel"
                >
                    <div
                        v-for="(img, index) in imageArray"
                        :key="index"
                        ref="elements"
                        class="slide"
                    >
                        <Image
                            :alt="img.alt"
                            :fetchpriority="
                                index === 0 && img.fetchpriority
                                    ? img.fetchpriority
                                    : 'low'
                            "
                            :loading="
                                index === 0 && img.loading
                                    ? img.loading
                                    : 'lazy'
                            "
                            :sources="img.sources"
                            :src="img.src"
                            fit="contain"
                        />
                    </div>
                </div>
            </div>
        </ThLink>

        <CarouselScrollIndicator
            :count="imageArray.length"
            :scroll-percent
            class="carousel-scroll-indicator"
        />

        <div
            v-if="imageArray.length > 1"
            class="buttons"
        >
            <ButtonRound
                :aria-label="t('catalogue.carousel.button_prev')"
                :disabled="isAtStart"
                class="button-round left"
                icon="angle-left"
                size="small"
                type="link"
                @click.prevent.stop="previous"
            />

            <ButtonRound
                :aria-label="t('catalogue.carousel.button_next')"
                :disabled="isAtEnd"
                class="button-round right"
                icon="angle-right"
                size="small"
                type="link"
                @click.prevent.stop="next"
            />
        </div>

        <div class="label-container">
            <LabelExpressAvailable v-if="supportsExpress" />

            <LabelLabGrown v-if="labGrown" />
        </div>

        <div class="save-for-later-wrapper">
            <slot name="save-for-later"></slot>
        </div>

        <div class="details">
            <div class="row-one">
                <h3 class="name">
                    {{ name }}
                </h3>

                <slot name="rating"></slot>
            </div>

            <slot name="metal-variation"></slot>

            <div
                v-if="description"
                class="description"
            >
                {{ description }}
            </div>

            <!-- TODO translate -->
            <div
                v-if="totalSalePrice"
                class="price"
            >
                <span v-if="isStartPrice">From </span>
                <Money
                    :data="totalSalePrice"
                    :formatter
                />
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import type { ButtonRouteRelative } from '@/components/ui/buttons/useThButton'
import type { MoneyFragment } from '@/graphql/api'
import { useMoneyFormatter } from '@/plugins/money-formatter.ts'
import { useCarousel } from '../carousels/base/useCarouselNew'

import ThLink from '@/components/ui/buttons/ThLink.vue'
import Money from '@/components/Money.vue'
import Image, { type ImageProps } from '@/components/Image.vue'
import LabelLabGrown from '../LabelLabGrown.vue'
import ButtonRound from '@/components/ui/buttons/ButtonRound.vue'
import CarouselScrollIndicator from '@/components/layout/carousels/base/CarouselScrollIndicator.vue'
import LabelExpressAvailable from '../LabelExpressAvailable.vue'

export interface ProductCardProps {
    name: string | undefined
    labGrown?: boolean
    supportsExpress?: boolean
    description?: string | undefined | null
    image?: ImageProps | ImageProps[]
    url?: ButtonRouteRelative | string
    totalSalePrice?: MoneyFragment
    isStartPrice?: boolean
}

const {
    description = undefined,
    image = undefined,
    totalSalePrice = undefined,
    url = undefined,
} = defineProps<ProductCardProps>()

defineEmits<{
    linkClicked: []
}>()

const { t } = useI18n()

const formatter = useMoneyFormatter()

const imageArray = computed(() =>
    image === undefined ? [] : Array.isArray(image) ? image : [image]
)

const {
    container,
    elements,
    next,
    previous,
    isAtEnd,
    isAtStart,
    scrollPercent,
} = useCarousel(computed(() => imageArray.value.length))
</script>

<style lang="scss" scoped>
.product-card {
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
    cursor: pointer;
    background: $white;

    .link-spacer {
        position: relative;
        width: 100%;
        aspect-ratio: 1;
    }

    .th-link {
        position: absolute;
        display: block;
        width: 100%;
        height: 100%;

        @include hover() {
            &:has(.image-wrapper:hover) ~ .buttons {
                opacity: 1;
            }
        }
    }

    .save-for-later-wrapper {
        position: absolute;
        top: 0;
        right: 2px;
        z-index: 1;
    }

    .carousel {
        position: relative;
        display: flex;
        overflow-x: auto;
        overflow-y: hidden;
        scroll-snap-type: x mandatory;
        width: 100%;
        -ms-overflow-style: none; /* IE and Edge */
        scrollbar-width: none; /* Firefox */

        outline-offset: -2px;

        @supports (-webkit-touch-callout: none) {
            /* Prevent slide vertical scroll in iOS */
            overscroll-behavior-y: none;
        }

        &::-webkit-scrollbar {
            display: none;
        }

        .slide {
            scroll-snap-align: start;
            flex: 0 0 100%;
            user-select: none;
            line-height: 0;
            background: #f9f9f9;
        }
    }

    .buttons {
        position: absolute;
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        aspect-ratio: 1;
        padding: 8px;
        pointer-events: none;
        opacity: 0;
        transition: opacity 340ms ease-in-out;

        &:has(.button-round:focus-visible) {
            opacity: 1;
        }

        @include min-width(1024) {
            &:hover {
                transition: opacity 120ms ease-out;

                opacity: 1;
            }
        }

        .button-round {
            pointer-events: all;
        }
    }

    .label-container {
        position: absolute;
        top: 10px;
        left: 10px;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 4px;
    }

    .image-wrapper {
        position: relative;
        width: 100%;
        aspect-ratio: 1/1;
        line-height: 0;
        background: #f9f9f9;

        :deep(.image) {
            mix-blend-mode: darken;
        }

        .carousel-scroll-indicator {
            position: absolute;
            bottom: 0;
        }
    }

    .details {
        width: 100%;
        display: flex;
        flex-wrap: wrap;
        padding: 20px 10px 10px;
        align-self: flex-end;

        @include min-width(360) {
            padding: 18px 0 0;
        }

        .row-one {
            display: flex;
            justify-content: space-between;
            gap: 20px;
            width: 100%;
            min-height: 20px;
            margin: 0 0 5px;

            @include min-width(640) {
                margin: 0 0 10px;
            }

            .name {
                font-family: $parcel-font-stack;
                letter-spacing: 0.01em;
                font-size: 1.125rem;
                line-height: 1em;
                color: $blue;

                @include min-width(640) {
                    font-size: 1.125rem;
                }
            }

            :slotted(.rating) {
                flex: 0 0 auto;
            }
        }

        .description {
            width: 100%;
            min-height: 58px;
            font-size: 0.875rem;
            margin: 0 0 10px;

            @include min-width(410) {
                min-height: 40px;
            }
        }

        .price {
            width: 100%;
            font-family: $effra-font-stack;
            color: $slate;
            text-transform: uppercase;
            font-size: 0.875rem;
            letter-spacing: 0.05rem;
        }
    }
}
</style>
